import React from 'react';
import { useIntl } from 'react-intl';
import { defineMessages } from 'react-intl.macro';

import { FilterList as FilterListIcon } from '@material-ui/icons';
import FilterList from 'core/Filters/FilterList';
import _ from 'lodash';
import { useProvidersByIdsV2 } from 'ui-model';

import ExpansionPanel, { ExpansionPanelProps } from './ExpansionPanel';
import TypographyWithIcon from './TypographyWithIcon';

const messages = defineMessages({
  summary: {
    id: 'components.v2.ExpansionPanelProviders.summary',
    defaultMessage: 'Providers ({count})',
  },
});

export interface ExpansionPanelProvidersProps extends Omit<ExpansionPanelProps, 'classes' | 'summary' | 'onChange'> {
  /* Array of provider IDs that populate the list with pre-filtered IDs */
  preFilteredProviders: string[];
  /* The value of the select; reflected by the selected options */
  value?: string[];
  /* Handle change selection on the select */
  onChange: (v: string[]) => void;
}

const ExpansionPanelProviders: React.FC<ExpansionPanelProvidersProps> = ({
  value = [],
  onChange,
  preFilteredProviders,
  ...rest
}) => {
  const { formatMessage } = useIntl();
  const { data: providers } = useProvidersByIdsV2({
    ids: preFilteredProviders,
    select: ['_id', 'name'],
    visible: true,
    schedulable: true,
    'settings.scheduler.enabled': true,
  });

  const options: any = _.map(providers, (provider) => ({ ...provider, id: provider._id }));

  const providerCounter = _.filter(value, (providerId) => _.some(providers, (provider) => provider._id === providerId))
    .length;

  return (
    <ExpansionPanel
      variant="outlined"
      summary={
        <TypographyWithIcon icon={<FilterListIcon fontSize="small" />} variant="body1">
          {formatMessage(messages.summary, { count: providerCounter })}
        </TypographyWithIcon>
      }
      {...rest}
    >
      <FilterList
        skipSwitch
        allItems={_.keyBy(options, 'id')}
        items={options}
        idsExcluded={value}
        onItemSelected={(id, selected) =>
          onChange(_.compact(selected ? _.concat(value, id) : _.without(value, id as string)))
        }
        onClearItemsSelected={() => onChange([])}
      />
    </ExpansionPanel>
  );
};

export default ExpansionPanelProviders;
